import { getDocs, query, where, orderBy, limit, startAfter, or, doc, getDoc, updateDoc, documentId, writeBatch } from "firebase/firestore";
import { projectsDB, projectsSchemeDB, permissionsDB, uploadAndSave, updateRecord } from "../../libs/firebase";
import { firebaseHelper } from "ui";
import { generateID, deepClone } from "../../libs/util";
import { BoardSchemeInterface, ProjectSchemeInterface, Permissions } from "ts-interfaces";
import { hasAccessToResource } from "ui/utils/usePermissions";

// Function to get user's own boards
export async function getBoardsScheme(
  lastDoc: any = null, 
  user_id: string, 
  pageSize = 15
): Promise<{ boards: BoardSchemeInterface[]; lastVisible: any }> {
  if (!projectsDB) return { boards: [], lastVisible: null };
  
  try {
    const q = query(
      projectsDB,
      where("user", "==", user_id),
      orderBy("updatedAt", "desc"),
      ...(lastDoc ? [startAfter(lastDoc)] : []), 
      limit(pageSize)
    );
    
    const snapshot = await getDocs(q);
    const lastVisible = snapshot.docs[snapshot.docs.length - 1] || null;
    
    return {
      boards: snapshot.docs.map(doc => doc.data() as BoardSchemeInterface),
      lastVisible
    };
  } catch (error) {
    console.error('Error fetching user boards:', error);
    return { boards: [], lastVisible: null };
  }
}

export async function getSharedBoards(
  permissions: Permissions | null
): Promise<BoardSchemeInterface[]> {
  if (!projectsDB || !permissions) return [];
  
  try {
    // Get shared board IDs from permissions
    const sharedBoardIds = [...new Set([
      ...(permissions.boardRead || []), 
      ...(permissions.boardWrite || [])
    ])];
    
    if (sharedBoardIds.length === 0) {
      return [];
    }
    
    const allSharedBoards: BoardSchemeInterface[] = [];
    
    // Fetch shared boards in batches of 10 (Firestore limit)
    for (let i = 0; i < sharedBoardIds.length; i += 10) {
      const batch = sharedBoardIds.slice(i, i + 10);
      const batchQuery = query(
        projectsDB,
        where(documentId(), "in", batch),
        orderBy("updatedAt", "desc")
      );
      
      const batchSnapshot = await getDocs(batchQuery);
      const batchBoards = batchSnapshot.docs.map(doc => doc.data() as BoardSchemeInterface);
      allSharedBoards.push(...batchBoards);
    }
    
    // Sort all boards by updatedAt
    allSharedBoards.sort((a, b) => a.updatedAt - b.updatedAt);

    return allSharedBoards;
  } catch (error) {
    console.error('Error fetching shared boards:', error);
    return [];
  }
}

export async function getBoardsForProject(
  projectId: string, 
  user_id: string, 
  permissions: Permissions | null
): Promise<BoardSchemeInterface[]> {
  if (!projectsDB || !projectId) return [];
  
  try {
    // Query boards where associatedProjectId equals the project ID
    const q = query(
      projectsDB,
      where("associatedProjectId", "==", projectId),
      orderBy("updatedAt", "desc")
    );
    
    const snapshot = await getDocs(q);
    const boards = snapshot.docs.map(doc => doc.data() as BoardSchemeInterface);
    return boards;
  } catch (error) {
    console.error('Error fetching boards for project:', error);
    return [];
  }
}

export async function getBoardsForProjects(
  projectIds: string[], 
  user_id: string, 
  permissions: Permissions | null
): Promise<Map<string, BoardSchemeInterface[]>> {
  if (!projectsDB || projectIds.length === 0) return new Map();
  
  try {
    const boardsByProject = new Map<string, BoardSchemeInterface[]>();
    
    // Initialize map with empty arrays
    projectIds.forEach(id => boardsByProject.set(id, []));

    // Fetch boards in batches
    for (let i = 0; i < projectIds.length; i += 10) {
      const batch = projectIds.slice(i, i + 10);
      const q = query(
        projectsDB,
        where("associatedProjectId", "in", batch),
        orderBy("updatedAt", "desc")
      );
      
      const snapshot = await getDocs(q);
      
      snapshot.docs.forEach(doc => {
        const board = doc.data() as BoardSchemeInterface;
        if (board.associatedProjectId) {
          const projectBoards = boardsByProject.get(board.associatedProjectId) || [];
          projectBoards.push(board);
          boardsByProject.set(board.associatedProjectId, projectBoards);
        }
      });
    }
    
    return boardsByProject;
  } catch (error) {
    console.error('Error fetching boards for projects:', error);
    return new Map();
  }
}

export async function renameBoard(boardId: string, newName: string, user_id:string, permissions: Permissions | null): Promise<void> {
  const docSnap = await getBoardIfAccessible(boardId, user_id, permissions);
  if (!docSnap) {
    throw new Error("No access to the board");
  }
  if ( !newName || newName?.length < 1 ) throw new Error("Invalid board name");
  await updateDoc(docSnap.ref, {"projectData.title": newName,updatedAt: Date.now()});
}

export async function copyBoard(board: BoardSchemeInterface, user_id:string, permissions: Permissions | null): Promise<BoardSchemeInterface> {
  const docSnap = await getBoardIfAccessible(board.id, user_id, permissions);
  const newId = generateID()
  const cloneBoard = deepClone(board)
  cloneBoard.id=newId;
  cloneBoard.projectData.projectId=newId
  cloneBoard.projectData.title = "Copy of " + cloneBoard.projectData.title
  // Reset these fields for the duplicate
  cloneBoard.associatedProjectId = "";
  cloneBoard.projectData.associatedProjectId = ""; // Also clear the nested one
  cloneBoard.projectData.userId = user_id;
  cloneBoard.user = user_id;
  cloneBoard.writeAccess= []; // Set to empty array as specified
  const duplicatedBoard: any = await uploadAndSave(cloneBoard.id,'projects',user_id, cloneBoard)
  return duplicatedBoard;
}

export async function copyBoardFromId(boardId: string, user_id:string, permissions: Permissions | null): Promise<BoardSchemeInterface> {
  const docSnap = await getBoardIfAccessible(boardId, user_id, permissions);
  if (!docSnap) {
    throw new Error("No access to the board");
  }
  const board = docSnap.data() as BoardSchemeInterface;
  const newId = generateID()
  const cloneBoard = deepClone(board)
  cloneBoard.id=newId;
  cloneBoard.projectData.projectId=newId
  cloneBoard.projectData.title = "Copy of " + cloneBoard.projectData.title
  cloneBoard.createdAt = Date.now();
  cloneBoard.updatedAt = Date.now();
  // Reset these fields for the duplicate
  cloneBoard.associatedProjectId = "";
  cloneBoard.projectData.associatedProjectId = ""; // Also clear the nested one
  cloneBoard.projectData.userId = user_id;
  cloneBoard.user = user_id;
  cloneBoard.writeAccess= []; // Set to empty array as specified
  const duplicatedBoard: any = await uploadAndSave(cloneBoard.id,'projects',user_id, cloneBoard)
  return duplicatedBoard;
}

export async function deleteBoard(boardId: string,user_id:string, permissions: Permissions | null): Promise<void> {
  const docSnap = await getBoardIfAccessible(boardId, user_id, permissions);
  await updateRecord(boardId, 'projects', {archive:true});
}

async function getBoardIfAccessible(boardId: string, user_id:string, permissions:Permissions | null)  {
  if (!user_id) throw new Error("User not authenticated");
  if (!boardId) throw new Error("Invalid board ID");
  
  try {
    const docSnap = await getDoc(doc(firebaseHelper.db, "projects", boardId));
    if (!docSnap.exists()) throw new Error("Board not found");
    const boardData = docSnap.data();  
    const hasAccess = hasAccessToResource(permissions, boardId, 'read', 'board', boardData.user || '', user_id);
    // if (boardData.user == user_id) return docSnap;
    // if (!permissions) return null;
    // const hasAccess = permissions.boardRead?.includes(boardId) || permissions.boardWrite?.includes(boardId);
    if (hasAccess) return docSnap
    return null;
  }
  catch {
    throw new Error("Board not found or no access");
  }

}

export async function associateBoardWithProject(
  boardId: string, 
  projectId: string | null, 
  user_id: string, 
  permissions: Permissions | null
): Promise<void> {
  const docSnap = await getBoardIfAccessible(boardId, user_id, permissions);
  if (!docSnap) {
    throw new Error("No access to the board");
  }
  
  const currentBoardData = docSnap.data() as BoardSchemeInterface;
  const currentWriteAccess = currentBoardData.writeAccess || [];
  
  let updatedWriteAccess = [...currentWriteAccess];
  
  // If projectId is provided, verify user has access to the project
  if (projectId) {
    const projectDoc = await getDoc(doc(firebaseHelper.db, "projectsScheme", projectId));
    if (!projectDoc.exists()) {
      throw new Error("Project not found");
    }
    
    const projectData = projectDoc.data() as ProjectSchemeInterface;
    const hasProjectAccess = hasAccessToResource(permissions, projectId, 'write', 'project', projectData.owner || '', user_id);
    
    if (!hasProjectAccess) {
      throw new Error("No access to the project");
    }
    
    // Get ALL users who have write access to this project
    const projectUsers = await getAllUsersWithProjectWriteAccess(firebaseHelper.db, projectId, projectData.owner);
    
    // Add all project users to writeAccess (avoiding duplicates)
    const writeAccessSet = new Set(updatedWriteAccess);
    projectUsers.forEach(userId => writeAccessSet.add(userId));
    updatedWriteAccess = Array.from(writeAccessSet);
  }
  // If removing from project (projectId is null), remove project users but keep direct board users
  else if (currentBoardData.associatedProjectId) {
    // Get the old project document to get its owner
    const oldProjectDoc = await getDoc(doc(firebaseHelper.db, "projectsScheme", currentBoardData.associatedProjectId));
    const oldProjectOwner = oldProjectDoc.exists() ? oldProjectDoc.data().owner : null;
  
    // Get users who had access via the old project
    const oldProjectUsers = await getAllUsersWithProjectWriteAccess(
      firebaseHelper.db, 
      currentBoardData.associatedProjectId,
      oldProjectOwner
    );
    
    // Get users who have direct board access
    const directBoardUsers = await getUsersWithDirectBoardAccess(firebaseHelper.db, boardId);
    const directBoardUsersSet = new Set(directBoardUsers);
    
    // Remove old project users who don't have direct board access
    updatedWriteAccess = updatedWriteAccess.filter(userId => 
      !oldProjectUsers.includes(userId) || directBoardUsersSet.has(userId)
    );
  }
  
  // Update the board
  await updateDoc(docSnap.ref, {
    'projectData.associatedProjectId': projectId,
    associatedProjectId: projectId,
    writeAccess: updatedWriteAccess,
    updatedAt: Date.now()
  });
}

export async function associateMultipleBoardsWithProject(
  boardsToAdd: string[], 
  boardsToRemove: string[], 
  projectId: string | null, 
  user_id: string, 
  permissions: Permissions | null
): Promise<void> {
  // Validate project access first if projectId is provided
  if (projectId) {
    const projectDoc = await getDoc(doc(firebaseHelper.db, "projectsScheme", projectId));
    if (!projectDoc.exists()) {
      throw new Error("Project not found");
    }
    
    const projectData = projectDoc.data() as ProjectSchemeInterface;
    const hasProjectAccess = hasAccessToResource(permissions, projectId, 'write', 'project', projectData.owner || '', user_id);
    
    if (!hasProjectAccess) {
      throw new Error("No access to the project");
    }
  }

  // Process all boards in parallel
  const allBoardIds = [...boardsToAdd, ...boardsToRemove];
  const boardPromises = allBoardIds.map(async (boardId) => {
    try {
      if (boardsToAdd.includes(boardId)) {
        // Associate with project
        await associateBoardWithProject(boardId, projectId, user_id, permissions);
      } else {
        // Remove from project (set to null)
        await associateBoardWithProject(boardId, null, user_id, permissions);
      }
    } catch (error) {
      console.error(`Error updating board ${boardId}:`, error);
      throw error;
    }
  });

  await Promise.all(boardPromises);
}

// Helper function to get ALL users with write access to a project
async function getAllUsersWithProjectWriteAccess(db: any, projectId: string, projectOwner: string): Promise<string[]> {
  if(!permissionsDB) return projectOwner ? [projectOwner] : [];
  try {
    const usersQuery = query(permissionsDB,
      where('projectWrite', 'array-contains', projectId)
    );
    const usersSnapshot = await getDocs(usersQuery);
    const permissionUsers = usersSnapshot.docs.map(doc => doc.id);

    const allUsers = new Set(permissionUsers);
    if (projectOwner) {
      allUsers.add(projectOwner);
    }
    return Array.from(allUsers);

  } catch (error) {
    console.error('Error getting users with project write access:', error);
    return projectOwner ? [projectOwner] : [];
  }
}

// Helper function to get users with direct board access
async function getUsersWithDirectBoardAccess(db: any, boardId: string): Promise<string[]> {
  if(!permissionsDB) return [];
  try {
    const usersQuery = query(
      permissionsDB,
      where('boardWrite', 'array-contains', boardId)
    );
    const usersSnapshot = await getDocs(usersQuery);
    return usersSnapshot.docs.map(doc => doc.id);
  } catch (error) {
    console.error('Error getting users with direct board access:', error);
    return [];
  }
}